import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { TextButton, Button } from '@wix/design-system';
import { AddSmall, Desktop, Mobile, Add } from '@wix/wix-ui-icons-common';
import Logo from '../../assets/images/wix-logo.svg';
import StudioLogo from '../../assets/images/studio-logo.svg';
import EditorXLogo from '../../assets/images/editor-x-logo.svg';
import PopoverHelp from './PopoverHelp/PopoverHelp';
import { useAppContext, ACTIONS } from '@components/Contexts/AppContext';
import { VIEW_MODE, EDITOR_TYPE } from '@src/utils/constants';
import Breakpoints from './Breakpoints';
import s from './Header.scss';
import useBiLogger from '../Hooks/useBiLogger';

const Header = () => {
  const { t } = useTranslation();
  const { logGenericClick } = useBiLogger();
  const {
    dispatch,
    state: {
      editorType,
      isPartner,
      accountLogo,
      viewMode,
      isSiteResponsive,
      breakpoints,
      comments,
      loading,
      errorData,
    },
  } = useAppContext();

  const isLoadingSite = false;
  const isSiteEditorTypeStudio = () => editorType === EDITOR_TYPE.STUDIO;

  function getTabClassName(type: any) {
    return type === viewMode ? s.active : '';
  }

  function renderViewMode() {
    return (
      <ul className={s.tabs}>
        <li className={getTabClassName(VIEW_MODE.DESKTOP)}>
          <TextButton
            dataHook="desktop-button"
            skin="dark"
            prefixIcon={<Desktop />}
            onClick={() => {
              logGenericClick({ click_type: 'desktop_view' });
              dispatch({
                type: ACTIONS.CHANGE_VIEW_MODE,
                payload: VIEW_MODE.DESKTOP,
              });
            }}
          >
            {t('get_feedback_header_view_type_button_desktop')}
          </TextButton>
        </li>
        <li className={getTabClassName(VIEW_MODE.MOBILE)}>
          <TextButton
            dataHook="mobile-button"
            skin="dark"
            prefixIcon={<Mobile />}
            onClick={() => {
              logGenericClick({ click_type: 'mobile_view' });
              dispatch({
                type: ACTIONS.CHANGE_VIEW_MODE,
                payload: VIEW_MODE.MOBILE,
              });
            }}
          >
            {t('get_feedback_header_view_type_button_mobile')}
          </TextButton>
        </li>
      </ul>
    );
  }

  function renderHeader() {
    if (_.isBoolean(isLoadingSite) && !isLoadingSite) {
      if (isSiteResponsive) {
        return <Breakpoints breakpoints={breakpoints!} />;
      } else {
        return renderViewMode();
      }
    }
    return <></>;
  }

  const renderAddCommentsSectionForNonStudioSites = () => {
    return (
      <>
        {!isSiteEditorTypeStudio() && (
          <Button
            dataHook="add-comment-button"
            prefixIcon={<Add />}
            onClick={e => {
              e.stopPropagation();
              logGenericClick({
                click_type: 'add_comment',
              });
              dispatch({
                type: ACTIONS.ADD_COMMENT,
              });
            }}
          >
            {t('get_feedback_header_add_comments_button')}
          </Button>
        )}
      </>
    );
  };

  const renderStudioSitesAddCommentsSection = () => {
    return (
      <div style={{ height: '100%' }}>
        <Button
          size="small"
          weight="normal"
          dataHook="add-comment-button"
          prefixIcon={<AddSmall />}
          className={s.commentsButton}
          onClick={e => {
            e.stopPropagation();
            logGenericClick({
              click_type: 'add_comment',
            });
            dispatch({
              type: ACTIONS.ADD_COMMENT,
            });
          }}
        >
          {t('get_feedback_header_add_comments_button')}
        </Button>
      </div>
    );
  };

  function renderPartnerOrWixLogo() {
    if (isPartner && accountLogo) {
      return (
        <img
          data-hook="partner-logo"
          height="30px"
          alt="partner-logo"
          src={accountLogo}
        />
      );
    }
    if (editorType === EDITOR_TYPE.EDITORX) {
      return <EditorXLogo data-hook="header-editor-x-logo" />;
    } else if (isSiteEditorTypeStudio()) {
      return <StudioLogo data-hook="header-studio-logo" />;
    } else {
      return <Logo data-hook="header-logo" />;
    }
  }

  return (
    <div
      data-hook="header"
      className={`${s.root} ${
        isSiteEditorTypeStudio() ? s.rootStudioBGColor : s.rootBGColor
      }`}
      onClick={() => {}}
    >
      <div className={s.logoSection}>{renderPartnerOrWixLogo()}</div>
      <div className={s.viewType}>{renderHeader()}</div>
      <div className={s.right}>
        {!loading && !errorData.showError && (
          <PopoverHelp editorType={editorType} savedComments={comments} />
        )}
        {renderAddCommentsSectionForNonStudioSites()}
        {isSiteEditorTypeStudio() && renderStudioSitesAddCommentsSection()}
      </div>
    </div>
  );
};

export default Header;
